import { Button, Dialog, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import AlertDialog from "elements/AlertDialog";

/*=========================================================
BUDGET ASSIGN - Asignar nuevo dinero disponible a un usuario

Cada asignación crea un registro nuevo (trazabilidad) y pasa a ser la
asignación activa del usuario. Pide confirmación con AlertDialog, igual
que el resto de los módulos.
=========================================================*/
export default function BudgetAssign(props: any) {
  const { setLoading, open, setOpen, user, loadList } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [formValues, setFormValues] = useState<any>({ amount: "", comments: "" });
  const [openAlert, setOpenAlert] = useState(false);

  useEffect(() => {
    if (open) {
      setFormValues({ amount: "", comments: "" });
    }
  }, [open]);

  const handleInputChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const currency = (value: any) =>
    `$${Number(value || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

  /*=========================================================
  VALIDATE BEFORE CONFIRMATION
  =========================================================*/
  const handleConfirm = () => {
    if (formValues.amount === "" || formValues.amount === null) {
      return enqueueSnackbar("Debes ingresar el dinero disponible", { variant: "error" });
    }
    if (isNaN(Number(formValues.amount))) {
      return enqueueSnackbar("El valor ingresado no es válido", { variant: "error" });
    }
    setOpenAlert(true);
  };

  /*=========================================================
  SUBMIT
  =========================================================*/
  const handleSubmit = async () => {
    setLoading(true);

    await ApiRequest({
      url: "/purchases/budgets/assign",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: {
        user_id: user?.user_id,
        amount: Number(formValues.amount),
        comments: formValues.comments,
      },
      setResponse: (response: any) => {
        if (response?.status === "success") {
          enqueueSnackbar("Dinero disponible asignado con éxito", { variant: "success" });
          setOpen(false);
          loadList && loadList();
        } else {
          enqueueSnackbar(response?.message || "Error al asignar el dinero disponible", {
            variant: "error",
          });
        }
        setLoading(false);
      },
    });
  };

  return (
    <>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <div className="DialogContainer" style={{ maxWidth: 620 }}>
          <div className="ModuleWrapper">
            <div className="boxContainerHeader">
              <div className="boxContainerTitle">
                <h2 className="h2_title">Nuevo dinero disponible</h2>
                <span className="h2_subtitle">{user?.user_name || ""}</span>
              </div>
            </div>

            {/* Estado actual del usuario */}
            {user?.has_budget && (
              <div
                style={{
                  backgroundColor: "#F6F6F7",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Dinero disponible actual: <b>{currency(user?.amount)}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Consumido: <b>{currency(user?.consumed)}</b>
                </Typography>
                <Typography
                  variant="body2"
                  style={{ color: Number(user?.available) < 0 ? "#d32f2f" : "#2e7d32" }}
                >
                  Restante: <b>{currency(user?.available)}</b>
                </Typography>
              </div>
            )}

            <div className="input_group">
              <TextField
                id="amount"
                name="amount"
                label="Dinero disponible"
                type="number"
                required
                fullWidth
                value={formValues.amount}
                onChange={handleInputChange}
                helperText="Este valor reemplaza el disponible actual y queda como el activo."
              />

              <TextField
                id="comments"
                name="comments"
                label="Observaciones"
                multiline
                rows={3}
                fullWidth
                value={formValues.comments}
                onChange={handleInputChange}
              />
            </div>

            <div className="dialogButtons">
              <Button onClick={() => setOpen(false)} variant="outlined">
                Cancelar
              </Button>
              <Button onClick={handleConfirm} variant="contained">
                Asignar
              </Button>
            </div>
          </div>
        </div>
      </Dialog>

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle={`¿Asignar ${currency(formValues.amount)} de dinero disponible a ${
          user?.user_name || "este usuario"
        }? El disponible anterior dejará de estar activo.`}
        send={handleSubmit}
      />
    </>
  );
}
